import { Button } from "@/src/components/buttons";
import {
  closeDrawer,
  openConfirmDialog,
  openDialog,
  openDrawer,
} from "@/src/components/dialogs";
import { Input } from "@/src/components/universalInput";
import {
  Appointment,
  AppointmentType,
  Hospitalisation,
  Patient,
  Protocol,
  ProtocolType,
  protocolTypes,
} from "@/src/data/allTypes";
import { CONFIG } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import {
  getItemId,
  getOptionsFromRecord,
  getServiceId,
  getUserId,
  uid,
} from "@/src/utils/utils";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useState } from "react";
import { ProtocolChoiceList } from "../admin/referentiel/adminProtocols";

export async function openAfterCureActions({
  appointment,
  item,
  onConfirm,
}: {
  appointment: Appointment;
  item: Hospitalisation;
  onConfirm: () => void;
}) {
  if (!appointment?.protocol) throw new Error("Protocol non défini");
  if (!item.patient) throw new Error("Patient non défini");

  async function finishAppointment() {
    appointment.status = "finished";
    await apiFetch("appointments", "addOne", {
      appointment,
    });
  }

  async function finishHospitalisation() {
    await apiFetch("hospitalisations", "addOne", {
      hospitalisation: {
        id: getItemId(item),
        status: "finished",
      },
    });
  }

  async function loadPendingAppointments() {
    return await apiFetch("appointments", "loadMany", {
      patientId: getItemId(item.patient),
      appointmentStatuses: ["scheduled", "rescheduled", "validated"],
      hasProtocol: true,
      protocolId: getItemId(appointment.protocol),
    });
  }

  async function deletePendingAppointments() {
    let pendingAppointments = await loadPendingAppointments();
    for (let appointment of pendingAppointments) {
      if (getItemId(appointment.item) == getItemId(item)) continue;
      await apiFetch("appointments", "deleteOne", {
        id: appointment.id,
      });
    }
  }
  await openDrawer({
    id: "cureActionsDrawer",
    title: "Actions",

    content: (
      <div className="c1 gap10">
        <Button
          onClick={async () => {
            // await finishAppointmentAndHospitalisation();
            await onConfirm();

            closeDrawer("cureActionsDrawer");
          }}
        >
          Enregistrer
        </Button>
        <Button
          type={"tertiary"}
          onClick={async () => {
            await finishAppointment();
            await finishHospitalisation();
            await onConfirm();

            closeDrawer("cureActionsDrawer");
          }}
        >
          Valider et faire sortir le patient
        </Button>
        <Button
          type={"tertiary"}
          onClick={async () => {
            let pendingAppointments = await loadPendingAppointments();

            await openCureFormDrawer({
              appointments: pendingAppointments,
              patient: item.patient!,
              protocol: appointment.protocol!,
              onConfirm: async () => {
                await finishHospitalisation();
                await onConfirm();
                closeDrawer("cureActionsDrawer");
              },
            });
          }}
        >
          Reporter
        </Button>
        <Button
          type={"tertiary"}
          onClick={async () => {
            await openConfirmDialog({
              id: "stopCureDialog",
              content:
                "Êtes-vous sûr de vouloir Changer le traitement ? \n \n Tous les rendez-vous en attente seront supprimés.",
              onConfirm: async () => {
                await finishAppointment();
                await finishHospitalisation();
                await deletePendingAppointments();

                await openProtocolChoiceDrawer({
                  patient: item.patient!,
                  onConfirm: async () => {
                    await onConfirm();
                    closeDrawer("cureActionsDrawer");
                  },
                });
              },
            });
          }}
        >
          Changer de traitement
        </Button>
        <Button
          type={"tertiary"}
          color="red"
          onClick={async () => {
            await openConfirmDialog({
              id: "stopCureDialog",
              content:
                "Êtes-vous sûr de vouloir arrêter le traitement ? \n \n Tous les rendez-vous en attente seront annulés.",
              onConfirm: async () => {
                await finishAppointment();
                await finishHospitalisation();
                await deletePendingAppointments();
                await onConfirm();
                closeDrawer("cureActionsDrawer");
              },
            });
          }}
        >
          Arrêter le traitement
        </Button>
      </div>
    ),
  });
}

async function openCureFormDrawer({
  patient,
  appointments,
  protocol,
  onConfirm,
}: {
  patient: Patient;
  appointments: Appointment[];
  protocol: Protocol;
  onConfirm: () => void;
}) {
  if (!protocol.appointmentType)
    throw new Error("Le protocol n'a pas de type de rendez-vous associé");

  await openDrawer({
    id: "curesDrawer",
    title: "Cures",
    content: (
      <CureForm
        currentAppointments={appointments}
        protocol={protocol}
        appointmentType={protocol.appointmentType}
        patient={patient}
        onChange={(newValue) => {
          appointments = newValue;
          console.log(appointments);
        }}
      />
    ),
    onConfirm: async () => {
      await apiFetch("appointments", "addMany", {
        appointments: getSimplifiedAppointments(appointments),
      });
      closeDrawer("curesDrawer");
      await onConfirm();
    },
  });
}

function getSimplifiedAppointments(appointments: Appointment[]) {
  return appointments.map((appointment) => {
    return {
      ...appointment,
      patient: getItemId(appointment.patient),
      creator: getItemId(appointment.creator),
      doctor: getItemId(appointment.doctor),
      service: getItemId(appointment.service),
      demandService: getItemId(appointment.demandService),
      protocol: getItemId(appointment.protocol),
      protocolDay: getItemId(appointment.protocolDay),
      protocolType: getItemId(appointment.protocolType),
    };
  });
}

function CureForm({
  patient,
  currentAppointments,
  appointmentType,
  protocol,
  onChange,
}: {
  patient: Patient;
  currentAppointments: Appointment[];
  appointmentType: AppointmentType;
  protocol: Protocol;
  onChange: (appointments: Appointment[]) => void;
}) {
  const [intercure, setIntercure] = useState(protocol.intercure);
  const [numberOfCycles, setNumberOfCycles] = useState(
    protocol.numberOfCycles || 6
  );
  const [protocolType, setProtocolType] = useState<ProtocolType>("adjuvant");
  const [protocolStartDate, setProtocolStartDate] = useState(
    moment().format("YYYY-MM-DD")
  );
  let [appointments, setAppointments] = useState<Appointment[]>(
    currentAppointments || getAppointments()
  );
  if (!appointments.length) appointments = getAppointments();

  let {
    data: counts,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["CureForm"],
    queryFn: () =>
      apiFetch("appointments", "loadAppointmentCounts", {
        typesIds: getItemId(CONFIG.get()?.chemotherapyType)
          ? [getItemId(CONFIG.get()?.chemotherapyType) || ""]
          : null,
        minDate: moment(protocolStartDate).format("YYYY-MM-DD"),
        maxDate: appointments[appointments.length - 1].start,
      }),
  });

  function getAppointments() {
    let localAppointments: Appointment[] = [];
    for (let i = 1; i <= numberOfCycles; i++) {
      for (let protocolDay of protocol.days) {
        if (!protocolDay) continue;

        let start = moment(
          moment(protocolStartDate).format("YYYY-MM-DD 00:00:00")
        )
          .add(intercure * (i - 1), "days")
          .add((protocolDay.day || 1) - 1, "days")
          .format("YYYY-MM-DD 09:00:00");

        localAppointments.push({
          patient,
          creator: getUserId(),
          service: getServiceId(),
          demandService: getServiceId(),
          demandContent: null,
          doctor: getUserId(),
          start,
          end: moment(start).add("30", "minutes").format("YYYY-MM-DD HH:mm:ss"),
          anchorDate: moment(start).format("YYYY-MM-DD"),
          type: appointmentType,
          protocolDay,
          protocolType,
          protocolCycle: i,
          protocol,
          item: null,
          status: "scheduled",
        });
      }
    }

    onChange([...localAppointments]);
    return [...localAppointments];
  }

  async function openCountsDialog({
    appointment,
  }: {
    appointment: Appointment;
  }) {
    let count = counts?.[moment(appointment.start).format("YYYY-MM-DD")];
    await openDialog({
      id: "appointmentCountsDialog",
      title: `Rendez-vous du ${moment(appointment.start).format("LL")}`,
      content: (
        <div className="c1 gap10">
          <div className="bold ">{count?.total || 0} RDV trouvé(s)</div>
          {Object.values(count?.types || {}).map((typeCount: any) => {
            return (
              <div key={uid()} className="card">
                <div className="c2 gap10">
                  <div>{typeCount.type?.name}</div>
                  <div className="tright bold">{typeCount.count} RDV</div>
                </div>
              </div>
            );
          })}
        </div>
      ),
    });
  }

  function getAppointmentsGroups(appointments: Appointment[]) {
    let appointmentGroups: Appointment[][] = [];
    for (let i = 1; i <= numberOfCycles; i++) {
      for (let appointment of appointments) {
        if (!appointment) continue;
        if (appointment.protocolCycle != i) continue;
        appointmentGroups[i] ||= [];
        appointmentGroups[i].push(appointment);
      }
    }
    return appointmentGroups;
  }

  return (
    <div className="c1 gap15">
      <Input
        name={"Type"}
        type={"select"}
        value={protocolType?.toString()}
        options={getOptionsFromRecord(protocolTypes)}
        onChange={(newValue) => {
          setProtocolType(newValue);
          setAppointments([]);
        }}
      />

      <Input
        name={"Nombre de cures"}
        type={"select"}
        value={numberOfCycles?.toString()}
        options={Array.from({ length: 60 }, (_, i) => i + 1).map((day) => {
          return { value: day?.toString(), label: `${day} cures` };
        })}
        onChange={(newValue) => {
          setNumberOfCycles(newValue);
          setAppointments([]);
        }}
      />

      <Input
        name={"Intercure"}
        type={"select"}
        value={intercure?.toString()}
        options={Array.from({ length: 60 }, (_, i) => i + 1).map((day) => {
          return { value: day?.toString(), label: `${day} jours` };
        })}
        onChange={(newValue) => {
          setIntercure(newValue);
          setAppointments([]);
        }}
      />

      <Input
        name={"Date de début du protocol"}
        type={"date"}
        value={protocolStartDate}
        onChange={() => {}}
        onBlur={(newValue) => {
          setProtocolStartDate(moment(newValue).format("YYYY-MM-DD"));
          setAppointments([]);
        }}
      />
      <div className="c1 gap30">
        {getAppointmentsGroups(appointments).map((group, index) => {
          if (!group?.length) return null;
          return (
            <div key={`group ${index}`} className="c1 gap10">
              <div className="bold f15 main-color">C{index}</div>
              <div className="c1 gap15">
                {group.map((appointment, ind) => {
                  let count =
                    counts?.[moment(appointment.start).format("YYYY-MM-DD")];

                  return (
                    <div
                      key={`appoiuntment${appointment.start}`}
                      className="card fade gap10"
                    >
                      <div className="c1-auto">
                        <div>
                          <strong>J{appointment.protocolDay?.day} </strong>
                          <span className="gray">
                            {moment(appointment.start).format("ddd")}
                          </span>
                        </div>
                        <div
                          className="bold"
                          onClick={async () => {
                            await openCountsDialog({ appointment });
                          }}
                        >
                          {count?.total || 0} RDV
                        </div>
                      </div>
                      <Input
                        name={null}
                        type={"date"}
                        value={moment(appointment.start).format("YYYY-MM-DD")}
                        onChange={(newValue) => {}}
                        onBlur={(newValue) => {
                          let diffDays = moment(
                            moment(newValue).format("YYYY-MM-DD 00:00:00")
                          ).diff(
                            moment(appointment.start).format(
                              "YYYY-MM-DD 00:00:00"
                            ),
                            "days"
                          );

                          let currentIndex = 0;
                          for (let i = 0; i < appointments.length; i++) {
                            let item = appointments[i];
                            if (
                              item.protocolDay == appointment.protocolDay &&
                              item.protocolCycle == appointment.protocolCycle
                            ) {
                              currentIndex = i;
                            }
                          }

                          for (
                            let i = currentIndex;
                            i < appointments.length;
                            i++
                          ) {
                            if (!appointments[i]) continue;
                            appointments[i].start = moment(
                              appointments[i].start
                            )
                              .add(diffDays, "days")
                              .format("YYYY-MM-DD 09:00:00");

                            appointments[i].anchorDate = moment(
                              appointments[i].start
                            ).format("YYYY-MM-DD");

                            appointments[i].end = moment(appointments[i].start)
                              .add("30", "minutes")
                              .format("YYYY-MM-DD HH:mm:ss");
                          }
                          setAppointments([...appointments]);
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export async function openProtocolChoiceDrawer({
  patient,
  onConfirm,
}: {
  patient: Patient;
  onConfirm: () => void;
}) {
  await openDrawer({
    id: "protocolDrawer",
    title: "Choisir un protocol",
    content: (
      <ProtocolChoiceList
        onSelect={async (protocol) => {
          await openCureFormDrawer({
            patient,
            protocol,
            appointments: [],
            onConfirm: async () => {
              closeDrawer("protocolDrawer");
              closeDrawer("appointmentTypesDrawer");
              await onConfirm();
            },
          });
        }}
      />
    ),
    footer: <div></div>,
  });
}
